import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ModalRef } from '@celerofinancas/ui-modals';
import { ShimmerComponent } from '@shared/components/shimmer/shimmer.component';
import {
  InvestmentModalData,
  DynamicModalConfig,
  InvestmentDetailsData,
} from './modal-content.interface';

/**
 * Investment details modal content component
 * Contains ONLY the content, no modal structure
 * Used as content for ModalService
 */
@Component({
  selector: 'caixa-webcomponent-investment-details-modal',
  templateUrl: './modal-content.component.html',
  styleUrls: ['./modal-content.component.scss'],
  standalone: true,
  imports: [ShimmerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvestmentDetailsModalContentComponent {
  /**
   * Error image source
   */
  public readonly errorImageSrc = '/assets/common/light/shared/error-image.svg';

  /**
   * Error state title
   */
  public readonly errorTitle = 'Tivemos um problema ao carregar as informações';

  /**
   * Error state subtitle
   */
  public readonly errorSubtitle = 'Não foi possível exibir os dados agora. Por favor, atualize a página e tente novamente.';

  /**
   * Modal reference injected from the modal service
   */
  public readonly modalRef = inject(ModalRef) as ModalRef<
    unknown,
    InvestmentModalData
  >;

  /**
   * Investment data passed to the modal
   */
  public get investmentData(): InvestmentDetailsData | null {
    return this.modalRef.data?.investmentData?.() ?? null;
  }

  /**
   * Modal loading state
   */
  public get isLoading(): boolean {
    return this.modalRef.data?.loading?.() ?? false;
  }

  /**
   * Modal error state
   */
  public get hasError(): boolean {
    return this.modalRef.data?.hasError?.() ?? false;
  }

  /**
   * Investment type for dynamic configuration
   */
  public get investmentType(): string {
    return this.modalRef.data?.investmentType || 'renda-fixa';
  }

  /**
   * Dynamic modal configuration based on investment type
   */
  public get modalConfig(): DynamicModalConfig {
    return this.getModalConfig(this.investmentType);
  }

  /**
   * Gets details value by key
   *
   * @param key - Property key to get value from
   * @returns Formatted value or fallback
   */
  public getDetailValue(key: keyof InvestmentDetailsData): string {
    if (!this.investmentData) {
      return 'N/A';
    }

    const value = this.investmentData[key];
    return value == null || value === '' ? 'N/A' : String(value);
  }

  /**
   * Get modal configuration based on investment type
   *
   * @param type - Investment type to get configuration for
   * @returns DynamicModalConfig for the specified type
   */
  private getModalConfig(type: string): DynamicModalConfig {
    const configs: Record<string, DynamicModalConfig> = {
      'renda-fixa': {
        title: 'Detalhe da Renda Fixa',
        sectionTitle: 'Informações do investimento',
        rows: [
          {
            items: [
              { label: 'Saldo Bruto', key: 'grossBalance', format: 'credit' },
              { label: 'Taxas/Impostos Provisionadas', key: 'provisionedFeesTaxes', format: 'debit' },
              { label: 'Saldo Líquido', key: 'netBalance', format: 'credit' }
            ]
          },
          {
            items: [
              { label: 'Data inicial do investimento', key: 'startDate' },
              { label: 'Data de vencimento', key: 'maturityDate' },
              { label: 'Data mínima para resgate', key: 'minimumRedemptionDate' }
            ]
          },
          {
            items: [
              { label: 'Periodicidade do rendimento', key: 'yieldPeriodicity' },
              { label: 'Indexador', key: 'indexer' },
              { label: 'Taxa acordada', key: 'agreedRate' }
            ]
          },
          {
            items: [
              { label: 'Quantidade de títulos', key: 'numberOfTitles' }
            ]
          }
        ]
      },
      'fundos': {
        title: 'Detalhe do Fundo de Investimento',
        sectionTitle: 'Informações do investimento',
        rows: [
          {
            items: [
              { label: 'CNPJ do Fundo de Investimento', key: 'cnpj' },
              { label: 'Categoria', key: 'category' },
              { label: 'Saldo Líquido', key: 'netBalance', format: 'credit' }
            ]
          },
          {
            items: [
              { label: 'Saldo Bruto', key: 'grossBalance', format: 'credit' },
              { label: 'Taxas Provisionadas', key: 'provisionedFeesTaxes', format: 'debit' },
              { label: 'Valor da Cota', key: 'shareValue', format: 'credit' }
            ]
          },
          {
            items: [
              { label: 'Quantidade de Cotas', key: 'numberOfShares' }
            ]
          }
        ]
      },
      'renda-variavel': {
        title: 'Detalhe da Renda Variável',
        sectionTitle: 'Informações do investimento',
        rows: [
          {
            items: [
              { label: 'Saldo Bruto', key: 'grossBalance', format: 'credit' },
              { label: 'Taxas/Impostos', key: 'provisionedFeesTaxes', format: 'debit' },
              { label: 'Saldo Líquido', key: 'netBalance', format: 'credit' }
            ]
          },
          {
            items: [
              { label: 'Data inicial', key: 'startDate' },
              { label: 'Quantidade de títulos', key: 'numberOfTitles' }
            ]
          }
        ]
      }
    };

    return configs[type] || configs['renda-fixa'];
  }
}
